/**
 * HoverTooltip — a hover-triggered tooltip rendered in a portal at document.body.
 *
 * WHY: the Scan-Outputs modal's target-breakdown tooltip (WCO-2414) lives inside an
 * `overflow-y-auto` body, so an in-flow absolute tooltip is clipped at the top (first row)
 * or bottom (last row) of the scroll viewport. Portaling to <body> with fixed positioning
 * escapes the overflow entirely; the panel flips above the trigger when it would overflow the
 * viewport bottom, so it's never cut off regardless of the row or list length.
 *
 * The trigger children render inline; `content` renders in the floating panel on hover.
 *
 * `tapToOpen` (WCO-2618): also open on click/tap and keep it open until the next tap on the
 * trigger, a tap outside, or Escape. Touch devices have no hover, so this makes the panel
 * reachable on mobile while leaving desktop hover intact (hover still opens/closes as before).
 */
import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface HoverTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;   // applied to the inline trigger wrapper
  panelClassName?: string; // applied to the floating panel (width, padding, colors)
  tapToOpen?: boolean;  // also toggle on click/tap + close on outside-tap/Escape (for touch)
  // Fired whenever the panel opens/closes (any path: hover, tap, outside-tap, Escape). Lets a
  // parent react to visibility — e.g. highlight the source rows a calculation tooltip refers to.
  onOpenChange?: (open: boolean) => void;
}

const GAP = 6;          // px between trigger and panel
const VIEWPORT_MARGIN = 8; // keep the panel this far from the viewport edges

const HoverTooltip: React.FC<HoverTooltipProps> = ({ content, children, className, panelClassName, tapToOpen, onOpenChange }) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // Whether the panel was opened by a tap (so a mouseleave doesn't dismiss it on hybrid devices).
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Notify the parent on every open/close transition, regardless of which path toggled it.
  useEffect(() => { onOpenChange?.(open); }, [open, onOpenChange]);

  // Position the panel once it's mounted (so we can measure its real size), relative to the
  // trigger. Right-align to the trigger; open downward, flipping above if it would overflow.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !panelRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const p = panelRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Right edge aligned to the trigger's right edge, clamped to the viewport.
    let left = t.right - p.width;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - p.width - VIEWPORT_MARGIN));

    // Prefer below the trigger; flip above if it would overflow the viewport bottom and there's
    // more room above.
    const below = t.bottom + GAP;
    const above = t.top - GAP - p.height;
    const overflowsBelow = below + p.height > vh - VIEWPORT_MARGIN;
    const top = overflowsBelow && above >= VIEWPORT_MARGIN ? above : below;

    setPos({ top, left });
  }, [open]);

  // When pinned open by a tap, close on an outside tap or Escape.
  useEffect(() => {
    if (!open || !pinned) return;
    const close = () => { setOpen(false); setPinned(false); setPos(null); };
    const onDown = (e: PointerEvent | MouseEvent) => {
      const target = e.target as Node;
      // Own trigger/panel: never close from here (the trigger's own onClick toggles it).
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      // A tap on ANOTHER tooltip's trigger/panel must not close this pinned one — tooltips are
      // independent, so several can stay open at once. Only a tap truly outside all tooltips closes.
      if (target instanceof Element && target.closest('[data-hovertooltip]')) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, pinned]);

  return (
    <span
      ref={triggerRef}
      data-hovertooltip="trigger"
      className={className}
      onMouseEnter={() => { if (content) setOpen(true); }}
      onMouseLeave={() => { if (!pinned) { setOpen(false); setPos(null); } }}
      onClick={tapToOpen ? (e) => {
        if (!content) return;
        e.preventDefault();
        e.stopPropagation();
        // Toggle: a tap on an open-pinned trigger closes it; otherwise open + pin.
        if (open && pinned) { setOpen(false); setPinned(false); setPos(null); }
        else { setOpen(true); setPinned(true); }
      } : undefined}
    >
      {children}
      {open && content && createPortal(
        <div
          ref={panelRef}
          role="tooltip"
          data-hovertooltip="panel"
          style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
          className={panelClassName}
        >
          {content}
        </div>,
        document.body,
      )}
    </span>
  );
};

export default HoverTooltip;
